// Shared react-query client factory.
//
// Extracted out of `main.tsx` (B-new-85) so the mutation-error policy is
// unit-testable — `main.tsx` is an app entry point and is excluded from the
// coverage/unit-test surface by `vitest.config.ts`.
//
// The policy it encodes: a mutation the server REJECTED must never look like
// a success or like nothing at all. Before this existed, the QueryClient had
// `retry:false` and no `mutationCache`/`queryCache` onError, and
// `RootErrorBoundary` (a React error boundary) cannot catch react-query's
// async rejections — so every write whose call site forgot an `onError`
// failed in silence.

import * as Sentry from '@sentry/react'
import { MutationCache, QueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import i18n from './i18n'
import { SKIP_GLOBAL_ERROR_TOAST } from './query-meta'

export interface QueryClientDeps {
  /** i18n lookup. Defaults to the app's initialised i18next instance. */
  translate?: (key: string) => string
  /** User-facing error surface. Defaults to a sonner error toast. */
  notifyError?: (message: string) => void
  /** Error reporter. Defaults to Sentry. */
  captureException?: (error: unknown, hint?: Record<string, unknown>) => void
}

/**
 * Best available human-readable text for a rejected mutation.
 *
 * tRPC surfaces the server's own message (e.g. the free-tier cap text from
 * `enforceFreeTierLimit`), which is far more actionable than a generic
 * string — prefer it whenever there is one.
 */
export function mutationErrorMessage(
  error: unknown,
  translate: (key: string) => string,
): string {
  const raw = rawErrorMessage(error)
  if (raw !== null) return isZodIssueJson(raw) ? translate('errors.invalidInput') : raw
  return translate('errors.somethingWentWrong')
}

/**
 * tRPC input-validation failures arrive as the serialised Zod issue array
 * (the API errorFormatter does not expose data.zodError), so detect by shape:
 * a non-empty array whose every element is an object with `code` and `path`.
 */
function isZodIssueJson(message: string): boolean {
  const trimmed = message.trim()
  if (!trimmed.startsWith('[')) return false
  try {
    const parsed: unknown = JSON.parse(trimmed)
    return (
      Array.isArray(parsed) &&
      parsed.length > 0 &&
      parsed.every(
        (issue) =>
          typeof issue === 'object' && issue !== null && 'code' in issue && 'path' in issue,
      )
    )
  } catch {
    return false
  }
}

function rawErrorMessage(error: unknown): string | null {
  if (error instanceof Error && error.message.trim() !== '') return error.message
  if (typeof error === 'string' && error.trim() !== '') return error
  if (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as { message: unknown }).message === 'string' &&
    (error as { message: string }).message.trim() !== ''
  ) {
    return (error as { message: string }).message
  }
  return null
}

// B-new-1523: tRPC rejections that are expected product outcomes, not defects.
// Mirrors apps/api/src/expected-gate-reason-codes.ts; the API errorFormatter
// exposes `reasonCode` in `error.data`, which is all the client can see.
const EXPECTED_GATE_REASON_CODES: ReadonlySet<string> = new Set([
  'AI_SEARCH_ONBOARDING_REQUIRED',
  'FREE_SAVED_JOBS_LIMIT_REACHED',
])

export function isExpectedTrpcError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false
  const data = (error as { data?: { code?: unknown; reasonCode?: unknown } | null }).data
  if (!data || typeof data !== 'object') return false
  if (data.code === 'BAD_REQUEST') return true
  return (
    data.code === 'FORBIDDEN' &&
    typeof data.reasonCode === 'string' &&
    EXPECTED_GATE_REASON_CODES.has(data.reasonCode)
  )
}

export function createQueryClient(deps: QueryClientDeps = {}): QueryClient {
  const translate = deps.translate ?? ((key: string) => i18n.t(key) as string)
  const notifyError =
    deps.notifyError ??
    ((message: string) => {
      toast.error(message)
    })
  const captureException =
    deps.captureException ??
    ((error: unknown, hint?: Record<string, unknown>) => {
      Sentry.captureException(error, hint as never)
    })

  return new QueryClient({
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        // Capture stays ahead of the opt-outs below (existing tested contract:
        // call sites with their own onError are still reported), but expected
        // gates and validation errors are not tracker errors (B-new-1523).
        if (!isExpectedTrpcError(error)) {
          captureException(error, {
            tags: { area: 'react-query', operation: 'mutation' },
            extra: { mutationKey: mutation.options.mutationKey },
          })
        }

        // Call sites that declare their own `onError` own the user-facing
        // messaging (upgrade dialogs, inline field errors, bespoke copy).
        // Reporting here as well would double-report. React Query invokes
        // this cache-level handler for EVERY mutation, before the
        // per-mutation one, so the opt-out has to live here.
        if (typeof mutation.options.onError === 'function') return

        // Same opt-out, for call sites that handle the error in a
        // `try/catch` around `mutateAsync` instead of via `onError` — that
        // form is invisible from here, so those sites must declare it.
        if (mutation.options.meta?.[SKIP_GLOBAL_ERROR_TOAST] === true) return

        notifyError(mutationErrorMessage(error, translate))
      },
    }),
    defaultOptions: {
      queries: {
        retry: false,
        refetchOnWindowFocus: true,
      },
      mutations: {
        retry: false,
      },
    },
  })
}
